/**
 * Contractor Suspension — IN-1.d
 * ──────────────────────────────
 * Pure writers that flip `Contractor.vettingStatus` between
 * "blocked" (suspended) and the prior state. Append-only suspension
 * audit log lives on the contractor itself (additive `suspensionLog`
 * array — see types.ts).
 *
 * IN-1.d is the FIRST module allowed to write `vettingStatus = "blocked"`
 * by an admin action. (Auto-suspension is still forbidden — expiry
 * sweep produces *advisory* suggestions only, never mutations.)
 *
 * Hard rules:
 *   • actor required
 *   • reason ≥ 10 chars
 *   • suspend is idempotent when already blocked (no-op, returns
 *     `{ changed: false }`)
 *   • reactivate is idempotent when not blocked (no-op)
 *   • reactivate restores prior status (trial / approved) recorded at
 *     suspend time; falls back to "trial" if no prior was recorded
 *   • derived dispatch-block (`isContractorExecutionBlocked`) reacts
 *     automatically — no separate write
 *
 * Anchor: RECONCILIATION.md §15 (IN-1.d)
 */

import type { Contractor } from "@/data/types";

export const MIN_SUSPENSION_REASON_LENGTH = 10;

export interface SuspensionLogEntry {
  id: string;
  action: "suspend" | "reactivate";
  reason: string;
  actor: string;
  at: string;
  /** Status the contractor was in immediately before this action. */
  priorStatus: NonNullable<Contractor["vettingStatus"]>;
}

let __suspCounter = 0;
function nextId(): string {
  __suspCounter += 1;
  return `susp-${Date.now().toString(36)}-${__suspCounter.toString(36)}`;
}

function requireNonEmpty(value: string | undefined, field: string): string {
  const v = (value ?? "").trim();
  if (!v) throw new Error(`suspension: ${field} is required.`);
  return v;
}

function requireReason(reason: string | undefined): string {
  const v = (reason ?? "").trim();
  if (v.length < MIN_SUSPENSION_REASON_LENGTH) {
    throw new Error(
      `suspension: reason must be at least ${MIN_SUSPENSION_REASON_LENGTH} characters.`,
    );
  }
  return v;
}

export interface SuspensionResult {
  contractor: Contractor;
  changed: boolean;
  entry?: SuspensionLogEntry;
}

export function suspendContractor(
  contractor: Contractor,
  reason: string,
  actor: string,
): SuspensionResult {
  if (!contractor) throw new Error("suspension: contractor is required.");
  const actorId = requireNonEmpty(actor, "actor");
  const trimmedReason = requireReason(reason);

  if (contractor.vettingStatus === "blocked") {
    return { contractor, changed: false };
  }
  const prior = (contractor.vettingStatus ?? "trial") as NonNullable<
    Contractor["vettingStatus"]
  >;
  const entry: SuspensionLogEntry = {
    id: nextId(),
    action: "suspend",
    reason: trimmedReason,
    actor: actorId,
    at: new Date().toISOString(),
    priorStatus: prior,
  };
  const next: Contractor = {
    ...contractor,
    vettingStatus: "blocked",
    suspensionLog: [...(contractor.suspensionLog ?? []), entry],
  };
  return { contractor: next, changed: true, entry };
}

export function reactivateContractor(
  contractor: Contractor,
  reason: string,
  actor: string,
): SuspensionResult {
  if (!contractor) throw new Error("suspension: contractor is required.");
  const actorId = requireNonEmpty(actor, "actor");
  const trimmedReason = requireReason(reason);

  if (contractor.vettingStatus !== "blocked") {
    return { contractor, changed: false };
  }
  // Look up the most recent suspend entry to restore prior status.
  const prior = [...(contractor.suspensionLog ?? [])]
    .reverse()
    .find((e) => e.action === "suspend")?.priorStatus ?? "trial";
  const entry: SuspensionLogEntry = {
    id: nextId(),
    action: "reactivate",
    reason: trimmedReason,
    actor: actorId,
    at: new Date().toISOString(),
    priorStatus: "blocked",
  };
  const next: Contractor = {
    ...contractor,
    vettingStatus: prior,
    suspensionLog: [...(contractor.suspensionLog ?? []), entry],
  };
  return { contractor: next, changed: true, entry };
}
